// ========================================
// pages/Security.js - Security Overview
// ========================================
// PROJECT: Agyapadie Vault - Digital Inheritance Platform
// LAST MODIFIED: April 11, 2026
// 
// PURPOSE: Inform users about our security measures, encryption, and data protection
// 
// ASSETS USED (from your inventory):
// - /images/symbols/golden-stool.png - Hero badge icon
// - /images/icons/feature-security.png - Security icons
// - /images/icons/feature-tradition.png - Authentication icons
// - /images/icons/feature-fraud.png - Protection icons
// - /images/icons/feature-trustees.png - Infrastructure icons
// 
// RESPONSIVE BREAKPOINTS:
// - Desktop: > 1024px (4-6 columns)
// - Tablet: 768px - 1024px (2-3 columns)
// - Mobile: < 768px (1 column, stacked layout)
// ========================================

import React from 'react';
import { Link } from 'react-router-dom';

const Security = () => {
  return (
    <div className="security-page">
      
      {/* ======================================== */}
      {/* SECTION 1: HERO - Fully Responsive */}
      {/* ======================================== */}
      <section className="security-hero">
        <div className="security-hero-overlay"></div>
        <div className="security-container">
          <div className="security-hero-content">
            <div className="security-badge">
              <img src="/images/symbols/golden-stool.png" alt="Golden Stool" className="security-badge-icon" />
              <span>Security First</span>
            </div>
            <h1 className="security-hero-title">
              Bank-Grade <span className="gold">Security</span>
            </h1>
            <p className="security-hero-subtitle">
              Your data is protected with multiple layers of encryption, cryptographic proofs, 
              and industry-leading security practices. We never compromise on security.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 2: ENCRYPTION STANDARDS - Responsive Grid */}
      {/* ======================================== */}
      <section className="security-encryption">
        <div className="security-container">
          <h2 className="security-section-title">Encryption <span className="gold">Standards</span></h2>
          <p className="security-section-subtitle">
            Military-grade encryption protects your data at every stage
          </p>
          
          <div className="security-encryption-grid">
            <div className="security-encryption-card">
              <img src="/images/icons/feature-security.png" alt="AES-256" className="security-encryption-icon" />
              <h3>AES-256 Encryption</h3>
              <p>Data at rest is encrypted using AES-256, the same standard used by banks and governments worldwide.</p>
              <div className="security-encryption-badge">Military Grade</div>
            </div>
            
            <div className="security-encryption-card">
              <img src="/images/icons/feature-tradition.png" alt="TLS 1.3" className="security-encryption-icon" />
              <h3>TLS 1.3</h3>
              <p>All data in transit is protected with TLS 1.3, the latest and most secure transport protocol.</p>
              <div className="security-encryption-badge">In Transit</div>
            </div>
            
            <div className="security-encryption-card">
              <img src="/images/icons/feature-fraud.png" alt="Key Management" className="security-encryption-icon" />
              <h3>Key Management</h3>
              <p>Encryption keys are stored separately from data and rotated regularly.</p>
              <div className="security-encryption-badge">Best Practice</div>
            </div>
            
            <div className="security-encryption-card">
              <img src="/images/icons/feature-trustees.png" alt="Cryptographic Hashing" className="security-encryption-icon" />
              <h3>Cryptographic Hashing</h3>
              <p>Passwords are hashed using bcrypt with per-user salts. We never store plain-text passwords.</p>
              <div className="security-encryption-badge">bcrypt + Salt</div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 3: AUTHENTICATION SECURITY - Responsive */}
      {/* ======================================== */}
      <section className="security-auth">
        <div className="security-container">
          <div className="security-auth-grid">
            <div className="security-auth-content">
              <h2 className="security-section-title">Authentication <span className="gold">Security</span></h2>
              <p>
                We implement multiple layers of authentication security to ensure only you can access your account.
              </p>
              
              <div className="security-auth-list">
                <div className="security-auth-item">
                  <div className="security-auth-icon">✓</div>
                  <div>
                    <h4>Strong Password Policy</h4>
                    <p>Minimum 6 characters, with complexity requirements enforced.</p>
                  </div>
                </div>
                
                <div className="security-auth-item">
                  <div className="security-auth-icon">⏰</div>
                  <div>
                    <h4>Session Management</h4>
                    <p>Automatic session timeout after inactivity. Tokens expire after 30 days.</p>
                  </div>
                </div>
                
                <div className="security-auth-item">
                  <div className="security-auth-icon">📧</div>
                  <div>
                    <h4>Email Verification</h4>
                    <p>Email must be verified before account access is granted.</p>
                  </div>
                </div>
                
                <div className="security-auth-item">
                  <div className="security-auth-icon">🚫</div>
                  <div>
                    <h4>Brute Force Protection</h4>
                    <p>Rate limiting and account lockout after multiple failed attempts.</p>
                  </div>
                </div>
                
                <div className="security-auth-item coming-soon">
                  <div className="security-auth-icon">🔐</div>
                  <div>
                    <h4>Two-Factor Authentication (2FA) <span className="coming-soon-badge">Coming Soon</span></h4>
                    <p>Optional 2FA using authenticator apps for additional account protection.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="security-auth-image">
              <div className="security-auth-stats">
                <div className="security-stat">
                  <div className="security-stat-number">100%</div>
                  <div className="security-stat-label">Passwords Hashed</div>
                </div>
                <div className="security-stat">
                  <div className="security-stat-number">24/7</div>
                  <div className="security-stat-label">Monitoring</div>
                </div>
                <div className="security-stat">
                  <div className="security-stat-number">30 Days</div>
                  <div className="security-stat-label">Token Expiry</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 4: DATA PROTECTION - Responsive Grid */}
      {/* ======================================== */}
      <section className="security-data">
        <div className="security-container">
          <h2 className="security-section-title white">Data <span className="gold">Protection</span></h2>
          <p className="security-section-subtitle white">
            Your data is protected at rest, in transit, and in backup
          </p>
          
          <div className="security-data-grid">
            <div className="security-data-card">
              <img src="/images/icons/feature-security.png" alt="Data at Rest" className="security-data-icon" />
              <h3>Data at Rest</h3>
              <p>AES-256 encrypted database storage. All sensitive fields are encrypted before storage.</p>
            </div>
            
            <div className="security-data-card">
              <img src="/images/icons/feature-tradition.png" alt="Data in Transit" className="security-data-icon" />
              <h3>Data in Transit</h3>
              <p>TLS 1.3 encryption for all network communication. Perfect forward secrecy enabled.</p>
            </div>
            
            <div className="security-data-card">
              <img src="/images/icons/feature-fraud.png" alt="Backup Encryption" className="security-data-icon" />
              <h3>Backup Encryption</h3>
              <p>Automated daily backups with AES-256 encryption. Backups are stored in secure, isolated locations.</p>
            </div>
            
            <div className="security-data-card">
              <img src="/images/icons/feature-trustees.png" alt="Secure Deletion" className="security-data-icon" />
              <h3>Secure Deletion</h3>
              <p>Data deletion follows NIST guidelines. Cryptographic erasure ensures data cannot be recovered.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 5: INFRASTRUCTURE SECURITY - Responsive */}
      {/* ======================================== */}
      <section className="security-infra">
        <div className="security-container">
          <div className="security-infra-grid">
            <div className="security-infra-content">
              <h2 className="security-section-title">Infrastructure <span className="gold">Security</span></h2>
              <p>
                Our infrastructure is built on secure, scalable, and compliant cloud providers.
              </p>
              
              <div className="security-infra-list">
                <div className="security-infra-item">
                  <span className="security-infra-icon">🛡️</span>
                  <span>DDoS Protection</span>
                </div>
                <div className="security-infra-item">
                  <span className="security-infra-icon">🔥</span>
                  <span>Web Application Firewall (WAF)</span>
                </div>
                <div className="security-infra-item">
                  <span className="security-infra-icon">🌐</span>
                  <span>Private Network Isolation</span>
                </div>
                <div className="security-infra-item">
                  <span className="security-infra-icon">📊</span>
                  <span>Real-time Intrusion Detection</span>
                </div>
                <div className="security-infra-item">
                  <span className="security-infra-icon">🔍</span>
                  <span>Vulnerability Scanning (Daily)</span>
                </div>
                <div className="security-infra-item">
                  <span className="security-infra-icon">📝</span>
                  <span>Security Information & Event Management (SIEM)</span>
                </div>
              </div>
            </div>
            
            <div className="security-infra-providers">
              <h3>Our Infrastructure Partners</h3>
              <div className="security-provider-logos">
                <div className="security-provider">MongoDB Atlas</div>
                <div className="security-provider">AWS/GCP/Azure</div>
                <div className="security-provider">CloudFlare</div>
              </div>
              <p className="security-provider-note">
                All providers are SOC 2, ISO 27001, and GDPR compliant.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 6: CRYPTOGRAPHIC PROOFS - Responsive */}
      {/* ======================================== */}
      <section className="security-crypto">
        <div className="security-container">
          <div className="security-crypto-card">
            <div className="security-crypto-icon">
              <img src="/images/icons/feature-security.png" alt="Cryptographic Proofs" className="security-crypto-img" />
            </div>
            <div className="security-crypto-content">
              <h2>Cryptographic Proofs & Blockchain Verification</h2>
              <p>
                Every asset you create generates a unique cryptographic hash and certificate number. 
                You can verify the integrity of your assets using our internal blockchain explorer, 
                similar to Polygon's explorer service.
              </p>
              <div className="security-crypto-features">
                <div className="security-crypto-feature">
                  <strong>✓ Asset Hash</strong> - Unique SHA-256 hash per asset
                </div>
                <div className="security-crypto-feature">
                  <strong>✓ Certificate Number</strong> - Tamper-proof certificate for each asset
                </div>
                <div className="security-crypto-feature">
                  <strong>✓ Integrity Checker</strong> - Verify your asset hasn't been modified
                </div>
                <div className="security-crypto-feature">
                  <strong>✓ Internal Blockchain Explorer</strong> - Coming soon for public verification
                </div>
              </div>
              <Link to="/verify-certificate" className="security-crypto-btn">Verify Asset Certificate →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 7: SECURITY PRACTICES - Responsive Grid */}
      {/* ======================================== */}
      <section className="security-practices">
        <div className="security-container">
          <h2 className="security-section-title">Security <span className="gold">Practices</span></h2>
          <p className="security-section-subtitle">
            We follow industry-leading security practices to protect your data
          </p>
          
          <div className="security-practices-grid">
            <div className="security-practice-card">
              <img src="/images/icons/feature-security.png" alt="Audits" className="security-practice-icon" />
              <h3>Regular Audits</h3>
              <p>Independent third-party security audits conducted annually.</p>
            </div>
            
            <div className="security-practice-card">
              <img src="/images/icons/feature-trustees.png" alt="Penetration Testing" className="security-practice-icon" />
              <h3>Penetration Testing</h3>
              <p>Quarterly penetration tests by certified ethical hackers.</p>
            </div>
            
            <div className="security-practice-card">
              <img src="/images/icons/feature-fraud.png" alt="Bug Bounty" className="security-practice-icon" />
              <h3>Bug Bounty Program</h3>
              <p>Rewards for responsible disclosure of security vulnerabilities.</p>
            </div>
            
            <div className="security-practice-card">
              <img src="/images/icons/feature-tradition.png" alt="Training" className="security-practice-icon" />
              <h3>Employee Training</h3>
              <p>Mandatory security awareness training for all employees.</p>
            </div>
            
            <div className="security-practice-card">
              <img src="/images/icons/feature-security.png" alt="Access Control" className="security-practice-icon" />
              <h3>Access Control</h3>
              <p>Least-privilege access model. Multi-party authorization for sensitive operations.</p>
            </div>
            
            <div className="security-practice-card">
              <img src="/images/icons/feature-trustees.png" alt="Monitoring" className="security-practice-icon" />
              <h3>Logging & Monitoring</h3>
              <p>Centralized logging with real-time alerting for suspicious activity.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 8: INCIDENT RESPONSE - Responsive */}
      {/* ======================================== */}
      <section className="security-incident">
        <div className="security-container">
          <div className="security-incident-card">
            <h2>Incident Response Plan</h2>
            <p>
              We have a documented incident response plan that is tested regularly. In the event of a security incident:
            </p>
            <div className="security-incident-steps">
              <div className="security-incident-step">
                <span className="step-number">1</span>
                <span>Detection</span>
              </div>
              <div className="security-incident-arrow">→</div>
              <div className="security-incident-step">
                <span className="step-number">2</span>
                <span>Containment</span>
              </div>
              <div className="security-incident-arrow">→</div>
              <div className="security-incident-step">
                <span className="step-number">3</span>
                <span>Eradication</span>
              </div>
              <div className="security-incident-arrow">→</div>
              <div className="security-incident-step">
                <span className="step-number">4</span>
                <span>Recovery</span>
              </div>
              <div className="security-incident-arrow">→</div>
              <div className="security-incident-step">
                <span className="step-number">5</span>
                <span>Notification</span>
              </div>
            </div>
            <p className="security-incident-note">
              <strong>Breach Notification:</strong> Affected users will be notified within 72 hours as required by GDPR and Data Protection Act 843.
            </p>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 9: USER SECURITY TIPS - Responsive Grid */}
      {/* ======================================== */}
      <section className="security-tips">
        <div className="security-container">
          <h2 className="security-section-title">User Security <span className="gold">Tips</span></h2>
          <p className="security-section-subtitle">
            Help us keep your account secure by following these best practices
          </p>
          
          <div className="security-tips-grid">
            <div className="security-tip-card">
              <div className="security-tip-icon">🔑</div>
              <h3>Use a Strong Password</h3>
              <p>Use a unique, complex password that you don't use elsewhere.</p>
            </div>
            
            <div className="security-tip-card">
              <div className="security-tip-icon">🔄</div>
              <h3>Enable 2FA (Coming Soon)</h3>
              <p>Add an extra layer of security with two-factor authentication.</p>
            </div>
            
            <div className="security-tip-card">
              <div className="security-tip-icon">📧</div>
              <h3>Verify Email Senders</h3>
              <p>Always verify that emails from us come from @agyapadie.com domain.</p>
            </div>
            
            <div className="security-tip-card">
              <div className="security-tip-icon">🚪</div>
              <h3>Log Out on Shared Devices</h3>
              <p>Always log out when using shared or public computers.</p>
            </div>
            
            <div className="security-tip-card">
              <div className="security-tip-icon">📱</div>
              <h3>Keep Contact Info Updated</h3>
              <p>Ensure your email and phone number are current for account recovery.</p>
            </div>
            
            <div className="security-tip-card">
              <div className="security-tip-icon">⚠️</div>
              <h3>Report Suspicious Activity</h3>
              <p>Contact us immediately if you notice unauthorized account access.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 10: REPORT A SECURITY ISSUE - Responsive */}
      {/* ======================================== */}
      <section className="security-report">
        <div className="security-container">
          <div className="security-report-card">
            <div className="security-report-icon">
              <img src="/images/icons/feature-security.png" alt="Report" className="security-report-img" />
            </div>
            <div className="security-report-content">
              <h3>Found a Security Vulnerability?</h3>
              <p>
                We take security seriously. If you discover a vulnerability, please report it to us 
                responsibly so we can address it promptly.
              </p>
              <div className="security-report-contact">
                <a href="mailto:security@agyapadie.com" className="security-report-email">
                  security@agyapadie.com
                </a>
                <span className="security-report-pgp">PGP Key Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* SECTION 11: CALL TO ACTION */}
      {/* ======================================== */}
      <section className="security-cta">
        <div className="security-container">
          <div className="security-cta-content">
            <h2 className="security-cta-title">Questions About Security?</h2>
            <p className="security-cta-subtitle">
              Our security team is here to answer your questions.
            </p>
            <div className="security-cta-buttons">
              <Link to="/contact" className="security-cta-primary">Contact Security Team</Link>
              <Link to="/compliance" className="security-cta-secondary">View Compliance</Link>
            </div>
          </div>
        </div>
      </section>

      {/* ======================================== */}
      {/* STYLES - Fully Responsive */}
      {/* ======================================== */}
      <style dangerouslySetInnerHTML={{ __html: `
        
        .security-page {
          font-family: 'Montserrat', sans-serif;
          background: white;
        }
        
        .security-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .gold {
          color: #FFD700;
        }
        
        .white {
          color: white;
        }
        
        /* Hero Section */
        .security-hero {
          background: #0A0A0A;
          position: relative;
          padding: 80px 0;
          text-align: center;
        }
        
        .security-hero-overlay {
          position: absolute;
          inset: 0;
          background: url('/images/patterns/hero-pattern.png');
          opacity: 0.05;
          pointer-events: none;
        }
        
        .security-hero-content {
          position: relative;
          z-index: 2;
        }
        
        .security-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,215,0,0.1);
          padding: 6px 16px;
          border-radius: 30px;
          margin-bottom: 24px;
        }
        
        .security-badge-icon {
          height: 20px;
          width: auto;
        }
        
        .security-badge span {
          color: #FFD700;
          font-size: 14px;
        }
        
        .security-hero-title {
          font-size: 52px;
          font-weight: bold;
          color: white;
          margin-bottom: 20px;
        }
        
        .security-hero-subtitle {
          font-size: 18px;
          color: #ccc;
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        /* Section Titles */
        .security-section-title {
          font-size: 36px;
          text-align: center;
          margin-bottom: 20px;
          color: #0A0A0A;
        }
        
        .security-section-subtitle {
          font-size: 18px;
          text-align: center;
          color: #666;
          margin-bottom: 60px;
        }
        
        .security-section-subtitle.white {
          color: #ccc;
        }
        
        /* Encryption Section - Responsive Grid */
        .security-encryption {
          padding: 80px 0;
          background: white;
        }
        
        .security-encryption-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }
        
        .security-encryption-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          transition: transform 0.3s;
        }
        
        .security-encryption-card:hover {
          transform: translateY(-5px);
        }
        
        .security-encryption-icon {
          height: 60px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .security-encryption-card h3 {
          font-size: 20px;
          margin-bottom: 15px;
        }
        
        .security-encryption-card p {
          color: #555;
          line-height: 1.6;
          margin-bottom: 15px;
        }
        
        .security-encryption-badge {
          display: inline-block;
          padding: 4px 12px;
          background: #FFD700;
          color: #0A0A0A;
          border-radius: 20px;
          font-size: 12px;
          font-weight: bold;
        }
        
        /* Authentication Section */
        .security-auth {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .security-auth-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        
        .security-auth-list {
          margin-top: 30px;
        }
        
        .security-auth-item {
          display: flex;
          gap: 15px;
          margin-bottom: 25px;
        }
        
        .security-auth-icon {
          width: 30px;
          height: 30px;
          background: #FFD700;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        .security-auth-item h4 {
          font-size: 16px;
          margin-bottom: 5px;
        }
        
        .security-auth-item p {
          font-size: 14px;
          color: #666;
        }
        
        .security-auth-item.coming-soon {
          opacity: 0.7;
        }
        
        .coming-soon-badge {
          font-size: 11px;
          background: #ddd;
          padding: 2px 8px;
          border-radius: 20px;
          margin-left: 10px;
        }
        
        .security-auth-stats {
          background: white;
          padding: 40px;
          border-radius: 15px;
          text-align: center;
        }
        
        .security-stat {
          margin-bottom: 25px;
        }
        
        .security-stat-number {
          font-size: 36px;
          font-weight: bold;
          color: #FFD700;
        }
        
        .security-stat-label {
          font-size: 14px;
          color: #666;
        }
        
        /* Data Protection - Responsive Grid */
        .security-data {
          padding: 80px 0;
          background: #0A0A0A;
        }
        
        .security-data-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
        }
        
        .security-data-card {
          background: rgba(255,255,255,0.05);
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          transition: transform 0.3s;
        }
        
        .security-data-card:hover {
          transform: translateY(-5px);
          background: rgba(255,255,255,0.1);
        }
        
        .security-data-icon {
          height: 50px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .security-data-card h3 {
          font-size: 18px;
          color: white;
          margin-bottom: 10px;
        }
        
        .security-data-card p {
          font-size: 14px;
          color: #ccc;
        }
        
        /* Infrastructure */
        .security-infra {
          padding: 80px 0;
          background: white;
        }
        
        .security-infra-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 60px;
          align-items: center;
        }
        
        .security-infra-list {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 15px;
          margin-top: 30px;
        }
        
        .security-infra-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px;
          background: #F5F0E6;
          border-radius: 10px;
          font-size: 14px;
        }
        
        .security-infra-icon {
          font-size: 20px;
        }
        
        .security-infra-providers {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
        }
        
        .security-infra-providers h3 {
          margin-bottom: 20px;
        }
        
        .security-provider-logos {
          display: flex;
          justify-content: center;
          gap: 20px;
          flex-wrap: wrap;
          margin-bottom: 15px;
        }
        
        .security-provider {
          padding: 8px 16px;
          background: white;
          border-radius: 10px;
          font-weight: bold;
          font-size: 14px;
        }
        
        .security-provider-note {
          font-size: 12px;
          color: #666;
        }
        
        /* Cryptographic Proofs */
        .security-crypto {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .security-crypto-card {
          background: white;
          border-radius: 15px;
          padding: 40px;
          display: flex;
          gap: 30px;
          align-items: flex-start;
          flex-wrap: wrap;
          border-left: 4px solid #FFD700;
        }
        
        .security-crypto-img {
          height: 60px;
          width: auto;
        }
        
        .security-crypto-content {
          flex: 1;
        }
        
        .security-crypto-content h2 {
          font-size: 28px;
          margin-bottom: 15px;
        }
        
        .security-crypto-content p {
          color: #555;
          margin-bottom: 20px;
          line-height: 1.6;
        }
        
        .security-crypto-features {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 10px;
          margin-bottom: 20px;
        }
        
        .security-crypto-feature {
          font-size: 14px;
          color: #444;
        }
        
        .security-crypto-btn {
          display: inline-block;
          padding: 12px 24px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .security-crypto-btn:hover {
          background: #B8860B;
        }
        
        /* Security Practices - Responsive Grid */
        .security-practices {
          padding: 80px 0;
          background: white;
        }
        
        .security-practices-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        .security-practice-card {
          background: #F5F0E6;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          transition: transform 0.3s;
        }
        
        .security-practice-card:hover {
          transform: translateY(-5px);
        }
        
        .security-practice-icon {
          height: 50px;
          width: auto;
          margin-bottom: 15px;
        }
        
        .security-practice-card h3 {
          font-size: 18px;
          margin-bottom: 10px;
        }
        
        .security-practice-card p {
          font-size: 14px;
          color: #666;
        }
        
        /* Incident Response */
        .security-incident {
          padding: 0 0 80px 0;
        }
        
        .security-incident-card {
          background: #0A0A0A;
          border-radius: 15px;
          padding: 40px;
          text-align: center;
        }
        
        .security-incident-card h2 {
          font-size: 28px;
          color: white;
          margin-bottom: 15px;
        }
        
        .security-incident-card p {
          color: #ccc;
          margin-bottom: 25px;
        }
        
        .security-incident-steps {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;
          gap: 15px;
          margin-bottom: 25px;
        }
        
        .security-incident-step {
          background: rgba(255,255,255,0.1);
          padding: 10px 20px;
          border-radius: 30px;
          display: flex;
          align-items: center;
          gap: 10px;
        }
        
        .step-number {
          width: 28px;
          height: 28px;
          background: #FFD700;
          color: #0A0A0A;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        
        .security-incident-arrow {
          color: #FFD700;
          font-size: 20px;
        }
        
        .security-incident-note {
          font-size: 14px;
          color: #888;
        }
        
        /* User Tips - Responsive Grid */
        .security-tips {
          padding: 80px 0;
          background: #F5F0E6;
        }
        
        .security-tips-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }
        
        .security-tip-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          text-align: center;
          transition: transform 0.3s;
        }
        
        .security-tip-card:hover {
          transform: translateY(-5px);
        }
        
        .security-tip-icon {
          font-size: 48px;
          margin-bottom: 15px;
        }
        
        .security-tip-card h3 {
          font-size: 18px;
          margin-bottom: 10px;
        }
        
        .security-tip-card p {
          font-size: 14px;
          color: #666;
        }
        
        /* Report Section */
        .security-report {
          padding: 0 0 80px 0;
        }
        
        .security-report-card {
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          border-radius: 15px;
          padding: 40px;
          display: flex;
          gap: 30px;
          align-items: center;
          flex-wrap: wrap;
        }
        
        .security-report-img {
          height: 60px;
          width: auto;
        }
        
        .security-report-content {
          flex: 1;
        }
        
        .security-report-content h3 {
          font-size: 24px;
          color: white;
          margin-bottom: 10px;
        }
        
        .security-report-content p {
          color: #ccc;
          margin-bottom: 15px;
        }
        
        .security-report-contact {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }
        
        .security-report-email {
          color: #FFD700;
          font-size: 18px;
          text-decoration: none;
          font-weight: bold;
        }
        
        .security-report-pgp {
          font-size: 12px;
          color: #888;
        }
        
        /* CTA */
        .security-cta {
          padding: 80px 0;
          background: linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%);
          text-align: center;
        }
        
        .security-cta-content {
          max-width: 700px;
          margin: 0 auto;
        }
        
        .security-cta-title {
          font-size: 32px;
          color: white;
          margin-bottom: 15px;
        }
        
        .security-cta-subtitle {
          font-size: 16px;
          color: #ccc;
          margin-bottom: 30px;
        }
        
        .security-cta-buttons {
          display: flex;
          gap: 20px;
          justify-content: center;
          flex-wrap: wrap;
        }
        
        .security-cta-primary {
          padding: 14px 32px;
          background: #FFD700;
          color: #0A0A0A;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .security-cta-primary:hover {
          background: #B8860B;
        }
        
        .security-cta-secondary {
          padding: 14px 32px;
          background: transparent;
          border: 2px solid #FFD700;
          color: #FFD700;
          text-decoration: none;
          border-radius: 5px;
          font-weight: bold;
          transition: all 0.3s;
        }
        
        .security-cta-secondary:hover {
          background: #FFD700;
          color: #0A0A0A;
        }
        
        /* ======================================== */
        /* RESPONSIVE BREAKPOINTS */
        /* ======================================== */
        
        /* Tablet: 1024px and below */
        @media (max-width: 1024px) {
          .security-hero-title {
            font-size: 40px;
          }
          
          .security-encryption-grid,
          .security-data-grid {
            grid-template-columns: repeat(2, 1fr);
          }
          
          .security-practices-grid,
          .security-tips-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        
        /* Mobile: 768px and below */
        @media (max-width: 768px) {
          .security-hero-title {
            font-size: 28px;
          }
          
          .security-hero-subtitle {
            font-size: 16px;
          }
          
          .security-section-title {
            font-size: 24px;
          }
          
          .security-section-subtitle {
            font-size: 14px;
          }
          
          .security-encryption-grid,
          .security-data-grid,
          .security-practices-grid,
          .security-tips-grid {
            grid-template-columns: 1fr;
          }
          
          .security-auth-grid,
          .security-infra-grid {
            grid-template-columns: 1fr;
          }
          
          .security-auth-stats {
            margin-top: 30px;
          }
          
          .security-infra-list {
            grid-template-columns: 1fr;
          }
          
          .security-crypto-card {
            text-align: center;
            flex-direction: column;
          }
          
          .security-crypto-features {
            grid-template-columns: 1fr;
          }
          
          .security-incident-steps {
            flex-direction: column;
          }
          
          .security-incident-arrow {
            transform: rotate(90deg);
          }
          
          .security-report-card {
            text-align: center;
            flex-direction: column;
          }
          
          .security-cta-title {
            font-size: 24px;
          }
          
          .security-cta-subtitle {
            font-size: 14px;
          }
          
          .security-cta-primary,
          .security-cta-secondary {
            padding: 12px 24px;
            font-size: 14px;
          }
          
          /* Touch-friendly tap targets */
          .security-crypto-btn,
          .security-cta-primary,
          .security-cta-secondary {
            min-height: 44px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
        }
        
        /* Small Mobile: 480px and below */
        @media (max-width: 480px) {
          .security-hero-title {
            font-size: 24px;
          }
          
          .security-section-title {
            font-size: 20px;
          }
          
          .security-encryption-card,
          .security-data-card,
          .security-practice-card,
          .security-tip-card {
            padding: 20px;
          }
          
          .security-crypto-btn {
            width: 100%;
            text-align: center;
          }
        }
        
      `}} />
      
    </div>
  );
};

export default Security;